import { Component, computed, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SiIconComponent } from '@siemens/element-ng/icon';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import type { ICountry } from '../../../../shared/interfaces/ICountry';
import type { ILocation } from '../../../../shared/interfaces/ILocation';
import { TabCommunicationComponent } from './tab-communication/tab-communication.component';
import { TabCountryInfoComponent } from './tab-country-info/tab-country-info.component';
import { TabDownloadsComponent } from './tab-downloads/tab-downloads.component';
import { TabLocationInfoComponent } from './tab-location-info/tab-location-info.component';
import { TabPersonComponent } from './tab-person/tab-person.component';
import { TabDirectionsComponent } from './tab-directions/tab-directions.component';
import type { PersonDetail } from '../../../../shared/interfaces/person-detail.interface';
import type { DrawerTab } from '../../../../shared/interfaces/drawer-tab.interface';

@Component({
    selector: 'app-drawer',
    templateUrl: './drawer.component.html',
    styleUrls: ['./drawer.component.scss'],
    imports: [
        SiTooltipDirective,
        SiLoadingSpinnerComponent,
        TabLocationInfoComponent,
        TabCommunicationComponent,
        TabCountryInfoComponent,
        TabDirectionsComponent,
        TabDownloadsComponent,
        TabPersonComponent,
        TranslatePipe,
        SiIconComponent
    ]
})
export class DrawerComponent {
    public readonly focusedLocation = input<ILocation | null>(null);
    public readonly focusedCountry = input<ICountry | null>(null);
    public readonly view = input<'location' | 'country'>('location');
    public readonly userDetail = input<PersonDetail | null>(null);
    public readonly loading = input(false);

    private readonly selectedTabId = signal<number | null>(null);

    public readonly tabs = computed<DrawerTab[]>(() => {
        if (this.view() === 'location') {
            const locationTabs: DrawerTab[] = [
                { id: 1, name: 'locationInfo', icon: 'element-info' },
                { id: 2, name: 'locationCommunication', icon: 'element-mail' },
                { id: 3, name: 'countryInfo', icon: 'element-global' },
                // {id: 4, name: 'directions', icon: 'element-go-to'},
                { id: 5, name: 'Downloads', icon: 'element-download' },
                { id: 6, name: 'userAddress', icon: 'element-address-book' }
            ];

            if (!this.userDetail()) {
                return locationTabs.filter(tab => tab.id !== 6);
            }

            return locationTabs;
        }

        return [
            { id: 3, name: 'Country information', icon: 'element-global' },
            { id: 4, name: 'Directions', icon: 'element-go-to' },
            { id: 5, name: 'Downloads', icon: 'element-download' }
        ];
    });

    public readonly selectedTab = computed(() => {
        const availableTabs = this.tabs();
        const selectedId = this.selectedTabId();

        if (selectedId && availableTabs.some(tab => tab.id === selectedId)) {
            return selectedId;
        }

        if (this.view() === 'location' && this.userDetail() && availableTabs.some(tab => tab.id === 6)) {
            return 6;
        }

        return availableTabs[0]?.id ?? 0;
    });

    public tabChanged(tabId: number): void {
        this.selectedTabId.set(tabId);
    }
}
